import React from 'react';
import { DropDownTreeComponent } from '@syncfusion/ej2-react-dropdowns';
import { BeforeCloseEventArgs, DialogComponent } from '@syncfusion/ej2-react-popups';
import ConfigurationField from './ConfigurationField';
import { ISelectSchema, SchemaFormError } from './types';
import useAsyncActionLock from '@shared/hooks/useAsyncActionLock';

declare var InovxLoader: any;

const stopInputKeyDownPropagation = (event: React.KeyboardEvent<HTMLInputElement>) => {
    event.stopPropagation();
};

interface AttributeDialogProps {
    isOpen: boolean;
    editAttribute: IAttributeDefinition | null;
    hasAddAttributeError: boolean;
    newAttributeName: string;
    newAttributeDataType: number;
    attributeDataTypeLabels: { [key: number]: string };
    isNewAttributeId: (id: string) => boolean;
    onInputChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onDataTypeChange: (value: number) => void;
    onSave: () => void | Promise<void>;
    onCancel: () => void;
}

interface SchemaDialogProps {
    isOpen: boolean;
    isSchemaPrimary: boolean;
    newSchemaName: string;
    dependencies: string[];
    schemaFormError: SchemaFormError;
    primarySchemas: ISelectSchema[];
    onInputChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onPrimarySchemaChange: (event: any) => void;
    onSave: () => void | Promise<void>;
    onCancel: () => void;
    onBeforeClose: (arg: BeforeCloseEventArgs) => void;
    getSchemaById: (schemaId: string | null | undefined) => ISchema | null;
}

interface AssetClassDialogProps {
    isOpen: boolean;
    mode: 'assetClass' | 'assetClassExtension' | 'annotationClass';
    editAssetClass: IAssetClassUpdateDetail | null;
    hasAssetClassError: boolean;
    newAssetClassName: string;
    availablePrimaryAssetClasses: Array<{ id: string; name: string }>;
    selectedPrimaryAssetClassID: string;
    assetClassInputRef: React.RefObject<HTMLInputElement | null>;
    onInputChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onPrimaryAssetClassChange: (value: string) => void;
    onSave: () => void | Promise<void>;
    onCancel: () => void;
}

interface FriendClassDialogProps {
    isOpen: boolean;
    editFriendClass: IFriendClass | null;
    hasFriendClassError: boolean;
    newFriendClassName: string;
    onInputChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onSave: () => void;
    onCancel: () => void;
}

export interface ConfigurationDialogsProps {
    attributeDialog: AttributeDialogProps;
    schemaDialog: SchemaDialogProps;
    assetClassDialog: AssetClassDialogProps;
    friendClassDialog: FriendClassDialogProps;
}

const ConfigurationDialogs: React.FC<ConfigurationDialogsProps> = ({ attributeDialog, schemaDialog, assetClassDialog, friendClassDialog }) => {
    const attributeSave = useAsyncActionLock(attributeDialog.onSave);
    const schemaSave = useAsyncActionLock(schemaDialog.onSave);
    const assetClassSave = useAsyncActionLock(assetClassDialog.onSave);
    const friendClassSave = useAsyncActionLock(friendClassDialog.onSave);

    return (
        <>
            {attributeDialog.isOpen && (
                <DialogComponent target={'#app'} width='350px' showCloseIcon={true} visible={true} isModal={true} zIndex={110000} header={() => (
                    <div>
                        <div className="e-icon-settings dlg-template">{attributeDialog.editAttribute === null ? InovxLoader.t('facility:form.addAttributeTitle') : InovxLoader.t('facility:form.editAttributeTitle')}</div>
                    </div>
                )} footerTemplate={() => (
                    <div>
                        <div className="d-flex j-cc">
                            <div>
                                <a id="facilityApp-save-btn" aria-disabled={attributeSave.isInFlight} onClick={attributeSave.isInFlight ? undefined : () => void attributeSave.run()} className={`btn btn-primary${attributeDialog.hasAddAttributeError || attributeSave.isInFlight ? ' disabled' : ''}`}>{InovxLoader.t('facility:preferences.saveButton')}</a>
                            </div>
                            <div className="ml-1">
                                <a id="facilityApp-cancel-btn" aria-disabled={attributeSave.isInFlight} onClick={attributeSave.isInFlight ? undefined : attributeDialog.onCancel} className={`btn btn-default${attributeSave.isInFlight ? ' disabled' : ''}`}>{InovxLoader.t('facility:preferences.cancelButton')}</a>
                            </div>
                        </div>
                    </div>
                )} close={attributeDialog.onCancel}>
                    <div className="dialogContent">
                        <ConfigurationField title={InovxLoader.t('facility:form.attributeName')} inputName="Name" className={attributeDialog.hasAddAttributeError ? 'has-error' : ''}>
                            <input type="text" autoFocus onChange={attributeDialog.onInputChange} onKeyDown={stopInputKeyDownPropagation} value={attributeDialog.newAttributeName} className="form-control" name="Name" placeholder={InovxLoader.t('facility:form.attributeNamePlaceholder')} autoComplete="off" />
                        </ConfigurationField>
                        <ConfigurationField title={InovxLoader.t('facility:form.dataType')} inputName="DataType" className="mt-1">
                            <select className="form-control" name="DataType" value={attributeDialog.newAttributeDataType} onChange={(event) => attributeDialog.onDataTypeChange(Number(event.target.value))} disabled={attributeDialog.editAttribute !== null && !attributeDialog.isNewAttributeId(attributeDialog.editAttribute.ID)}>
                                {Object.entries(attributeDialog.attributeDataTypeLabels).map(([key, label]) => (
                                    <option key={key} value={key}>{label}</option>
                                ))}
                            </select>
                        </ConfigurationField>
                    </div>
                </DialogComponent>
            )}

            {schemaDialog.isOpen && (
                <DialogComponent className="configuration-dialog" target={'#app'} width='350px' showCloseIcon={true} visible={true} isModal={true} zIndex={110000} beforeClose={schemaDialog.onBeforeClose} header={() => (
                    <div>
                        <div className="e-icon-settings dlg-template">{schemaDialog.isSchemaPrimary ? InovxLoader.t('facility:form.addSchemaTitle') : InovxLoader.t('facility:form.addSchemaExtensionTitle')}</div>
                    </div>
                )} footerTemplate={() => (
                    <div>
                        <div className="d-flex j-cc">
                            <div>
                                <a id="configform-save-schema-btn" aria-disabled={schemaSave.isInFlight} onClick={schemaSave.isInFlight ? undefined : () => void schemaSave.run()} className={`btn btn-primary${schemaSave.isInFlight ? ' disabled' : ''}`}>{InovxLoader.t('facility:preferences.saveButton')}</a>
                            </div>
                            <div className="ml-1">
                                <a id="configform-cancel-schema-btn" aria-disabled={schemaSave.isInFlight} onClick={schemaSave.isInFlight ? undefined : schemaDialog.onCancel} className={`btn btn-default${schemaSave.isInFlight ? ' disabled' : ''}`}>{InovxLoader.t('facility:preferences.cancelButton')}</a>
                            </div>
                        </div>
                    </div>
                )} close={schemaDialog.onCancel}>
                    <div className="dialogContent">
                        <ConfigurationField title={schemaDialog.isSchemaPrimary ? InovxLoader.t('facility:form.schemaName') : InovxLoader.t('facility:form.subSchemaName')} inputName="SchemaName" className={`mt-1 ${schemaDialog.schemaFormError.schemaName ? 'has-error' : ''}`}>
                            <input type="text" autoFocus onChange={schemaDialog.onInputChange} onKeyDown={stopInputKeyDownPropagation} value={schemaDialog.newSchemaName} className="form-control" name="SchemaName" placeholder={schemaDialog.isSchemaPrimary ? InovxLoader.t('facility:form.schemaNamePlaceholder') : InovxLoader.t('facility:form.schemaExtensionNamePlaceholder')} autoComplete="off" />
                        </ConfigurationField>
                        {!schemaDialog.isSchemaPrimary && (
                            (schemaDialog.dependencies && schemaDialog.dependencies.length === 1) ? (
                                <ConfigurationField title={InovxLoader.t('facility:form.primarySchema')} inputName="PrimarySchema" className={`mt-1 ${schemaDialog.schemaFormError.primarySchema ? 'has-error' : ''}`}>
                                    <input className="form-control" value={schemaDialog.getSchemaById(schemaDialog.dependencies[0])?.Name || ''} readOnly />
                                </ConfigurationField>
                            ) : (
                                <ConfigurationField title={InovxLoader.t('facility:form.primarySchema')} inputName="PrimarySchema" className={`mt-1 ${schemaDialog.schemaFormError.primarySchema ? 'has-error' : ''}`}>
                                    <DropDownTreeComponent className="multi-select-box" fields={{ dataSource: schemaDialog.primarySchemas, value: 'id', text: 'name' }} allowMultiSelection={true} showCheckBox={true} placeholder={InovxLoader.t('facility:form.selectPrimarySchema')} popupHeight="200px" value={schemaDialog.dependencies} change={schemaDialog.onPrimarySchemaChange} />
                                </ConfigurationField>
                            )
                        )}
                    </div>
                </DialogComponent>
            )}

            {assetClassDialog.isOpen && (
                <DialogComponent className="configuration-dialog" target={'#app'} width='350px' showCloseIcon={true} visible={true} isModal={true} zIndex={110000} header={() => (
                    <div>
                        <div className="e-icon-settings dlg-template">{assetClassDialog.mode === 'assetClassExtension' ? InovxLoader.t('facility:form.addAssetClassExtensionTitle') : (assetClassDialog.mode === 'annotationClass' ? (assetClassDialog.editAssetClass === null ? InovxLoader.t('facility:form.addAnnotationClassTitle') : InovxLoader.t('facility:form.editAnnotationClassTitle')) : (assetClassDialog.editAssetClass === null ? InovxLoader.t('facility:form.addAssetClassTitle') : InovxLoader.t('facility:form.editAssetClassTitle')))}</div>
                    </div>
                )} footerTemplate={() => (
                    <div>
                        <div className="d-flex j-cc">
                            <div>
                                <a id="facilityApp-save-assetclass-btn" aria-disabled={assetClassSave.isInFlight} onClick={assetClassSave.isInFlight ? undefined : () => void assetClassSave.run()} className={`btn btn-primary${(((assetClassDialog.mode === 'assetClass' || assetClassDialog.mode === 'annotationClass') ? assetClassDialog.hasAssetClassError : !assetClassDialog.selectedPrimaryAssetClassID) || assetClassSave.isInFlight) ? ' disabled' : ''}`}>{InovxLoader.t('facility:preferences.saveButton')}</a>
                            </div>
                            <div className="ml-1">
                                <a id="facilityApp-cancel-assetclass-btn" aria-disabled={assetClassSave.isInFlight} onClick={assetClassSave.isInFlight ? undefined : assetClassDialog.onCancel} className={`btn btn-default${assetClassSave.isInFlight ? ' disabled' : ''}`}>{InovxLoader.t('facility:preferences.cancelButton')}</a>
                            </div>
                        </div>
                    </div>
                )} close={assetClassDialog.onCancel}>
                    <div className="dialogContent">
                        {assetClassDialog.mode === 'assetClassExtension' ? (
                            <ConfigurationField title={InovxLoader.t('facility:form.primaryClass')} inputName="PrimaryAssetClassID">
                                <select
                                    autoFocus
                                    className="form-control"
                                    name="PrimaryAssetClassID"
                                    value={assetClassDialog.selectedPrimaryAssetClassID}
                                    onChange={(event) => assetClassDialog.onPrimaryAssetClassChange(event.target.value)}
                                >
                                    {assetClassDialog.availablePrimaryAssetClasses.map((assetClass) => (
                                        <option key={assetClass.id} value={assetClass.id}>{assetClass.name}</option>
                                    ))}
                                </select>
                            </ConfigurationField>
                        ) : (
                            <ConfigurationField title={InovxLoader.t(assetClassDialog.mode === 'annotationClass' ? 'facility:form.annotationClass' : 'facility:form.assetClass')} inputName="AssetClassName" className={assetClassDialog.hasAssetClassError ? 'has-error' : ''}>
                                <input
                                    ref={assetClassDialog.assetClassInputRef}
                                    type="text"
                                    autoFocus
                                    onChange={assetClassDialog.onInputChange}
                                    onKeyDown={stopInputKeyDownPropagation}
                                    value={assetClassDialog.newAssetClassName}
                                    className="form-control"
                                    name="AssetClassName"
                                    placeholder={InovxLoader.t(assetClassDialog.mode === 'annotationClass' ? 'facility:form.annotationClassNamePlaceholder' : 'facility:form.assetClassNamePlaceholder')}
                                    autoComplete="off"
                                />
                            </ConfigurationField>
                        )}
                    </div>
                </DialogComponent>
            )}

            {friendClassDialog.isOpen && (
                <DialogComponent target={'#app'} width='350px' showCloseIcon={true} visible={true} isModal={true} zIndex={110000} header={() => (
                    <div>
                        <div className="e-icon-settings dlg-template">{friendClassDialog.editFriendClass === null ? InovxLoader.t('facility:form.addFriendClassTitle') : InovxLoader.t('facility:form.editFriendClassTitle')}</div>
                    </div>
                )} footerTemplate={() => (
                    <div>
                        <div className="d-flex j-cc">
                            <div>
                                <a id="facilityApp-save-friendclass-btn" aria-disabled={friendClassSave.isInFlight} onClick={friendClassSave.isInFlight ? undefined : () => void friendClassSave.run()} className={`btn btn-primary${friendClassDialog.hasFriendClassError || friendClassSave.isInFlight ? ' disabled' : ''}`}>{InovxLoader.t('facility:preferences.saveButton')}</a>
                            </div>
                            <div className="ml-1">
                                <a id="facilityApp-cancel-friendclass-btn" aria-disabled={friendClassSave.isInFlight} onClick={friendClassSave.isInFlight ? undefined : friendClassDialog.onCancel} className={`btn btn-default${friendClassSave.isInFlight ? ' disabled' : ''}`}>{InovxLoader.t('facility:preferences.cancelButton')}</a>
                            </div>
                        </div>
                    </div>
                )} close={friendClassDialog.onCancel}>
                    <div className="dialogContent">
                        <ConfigurationField title={InovxLoader.t('facility:form.friendClassName')} inputName="FriendClassName" className={friendClassDialog.hasFriendClassError ? 'has-error' : ''}>
                            <input type="text" autoFocus onChange={friendClassDialog.onInputChange} onKeyDown={stopInputKeyDownPropagation} value={friendClassDialog.newFriendClassName} className="form-control" name="FriendClassName" placeholder={InovxLoader.t('facility:form.friendClassNamePlaceholder')} autoComplete="off" />
                        </ConfigurationField>
                    </div>
                </DialogComponent>
            )}
        </>
    );
};

export default ConfigurationDialogs;
